import {
    eqItemClasses,
    eqItemSlots,
    getBonusFriendlyName,
    getItemBonusesByClassGroup,
    getItemClassGroupByClass,
    ItemClass,
    ItemClassGroup,
    ItemExtendedID,
    itemExtendedIDToString,
    parseItemExtendedID,
    parseItemStat,
    StoredItem,
    writeItem,
} from "LibMargoWorld/item";
import { MargoURLs } from "LibMargoWorld/micc";

import { getEqItemBySlot } from "../eq-set";
import { createItemDisplay } from "../item-loader";
import { appendComparisonNote } from "../tips";

declare function _t(t: string, p: object): string;

const outfitPreviewOffsets = [
    [0, 0],
    [-1, 0],
    [-2, 0],
    [-3, 0],
    [0, 0],
    [-1, 0],
    [-2, 0],
    [-3, 0],
    [0, -1],
    [-1, -1],
    [-2, -1],
    [-3, -1],
    [0, -1],
    [-1, -1],
    [-2, -1],
    [-3, -1],
    [0, -3],
    [-1, -3],
    [-2, -3],
    [-3, -3],
    [0, -3],
    [-1, -3],
    [-2, -3],
    [-3, -3],
    [0, -2],
    [-1, -2],
    [-2, -2],
    [-3, -2],
    [0, -2],
    [-1, -2],
    [-2, -2],
    [-3, -2],
];

function lvlAdjustHandler(info: ExtendedItemInfo) {
    if (!info.data.hasLvlAdjust) {
        info.addLabel("Zmień wyświetlany poziom:");
        info.addHTML(`<div class="d-flex">`);
        info.addHTML(`<input class="me-1 form-control form-control-sm autolvl-change-input" type="number" value="${info.stat.lvl ?? 1}"> `);
        info.addHTML(`<button class="btn btn-secondary btn-sm autolvl-change-submit" data-item-info="${info.item.id}">Zmień</button>`);
        info.addHTML(`</div>`);
        info.data.hasLvlAdjust = true;
    }
}

const statHandlers: Record<string, (info: ExtendedItemInfo) => void> = {
    outfit: (info) => {
        info.addLabel("Podgląd outfitu");
        const [_time, url] = info.stat.outfit.split(",");
        info.addOutfitPreviews([url]);
    },
    outfit_selector: (info) => {
        info.addLabel("Podgląd outfitów do wybrania");
        const urls = info.stat.outfit_selector
            .split("|")
            .slice(1)
            .map((e) => e.split(",")[0]);
        info.addOutfitPreviews(urls);
    },
    lvlupgs: lvlAdjustHandler,
};

const tagHandlers: Record<string, (info: ExtendedItemInfo) => void> = {
    autolvl: lvlAdjustHandler,
};

function getCurrentPageItemID() {
    const id = document.location.href.match(/view\/([^/]*)/)?.[1];
    if (!id) {
        return null;
    } else {
        return parseItemExtendedID(id);
    }
}

function navigateToItemID(id: ItemExtendedID) {
    const urlNamePart = document.location.href.match(/view\/([^/]*)\/(.*)/)?.[2];
    document.location.href = `/item/view/${itemExtendedIDToString(id)}/${urlNamePart ?? ""}`;
}

function handleButtonClick($btt: HTMLButtonElement) {
    if ($btt.classList.contains("autolvl-change-submit") || $btt.classList.contains("upgrade-change-btn") || $btt.classList.contains("bonus-change-submit")) {
        const info = getCurrentPageItemID();
        const lvlInput = <HTMLInputElement>document.querySelector(".autolvl-change-input");
        // const upgradeInput = (<HTMLInputElement>document.querySelector(".upgrade-change-input"));
        const upgradeLevel = parseInt($btt.dataset.upgrade);
        const bounsInput = <HTMLInputElement>document.querySelector(".bonus-change-input");
        if (lvlInput) {
            info.lvlOverride = parseInt(lvlInput.value);
        }
        if (!isNaN(upgradeLevel)) {
            info.upgrade = upgradeLevel;
        }
        if (bounsInput && !bounsInput.getAttribute("disabled")) {
            info.upgradeBonus = bounsInput.value;
        }
        if (info.lvlOverride < 0 || isNaN(info.lvlOverride)) {
            alert(`Niepoprawny poziom przedmiotu!`);
        } else if (info.upgrade < 0 || info.upgrade > 5 || isNaN(info.upgrade)) {
            alert(`Niepoprawne ulepszenie przedmiotu!`);
        } else {
            navigateToItemID(info);
        }
    } else if ($btt.classList.contains("compare-item")) {
        const $wrapper = document.querySelector(".item-display-wrapper");
        if (!$wrapper) {
            console.error(`.item-display-wrapper was not found`);
            return;
        }
        $btt.setAttribute("disabled", "");
        $btt.classList.remove("compare-item");
        const item = getEqItemBySlot(eqItemSlots[parseInt($btt.dataset.cl)]);
        const disp = createItemDisplay(item);
        appendComparisonNote(disp);
        $wrapper.prepend(disp);
    }
}

function handleSymbolClick($symbol: HTMLElement) {
    const info = getCurrentPageItemID();
    if ($symbol.classList.contains("selected")) {
        info.socketContent = 0;
    } else {
        info.socketContent = Number($symbol.dataset.symbolId);
    }
    navigateToItemID(info);
}

export class ExtendedItemInfo {
    static initDone = false;
    static globalInit() {
        if (this.initDone) {
            return;
        }
        this.initDone = true;
        this.animatePreviews();
        setInterval(this.animatePreviews.bind(this), 1000 / 5);
        document.addEventListener("click", (e) => {
            if (e.target instanceof HTMLButtonElement && e.target.dataset.itemInfo) {
                handleButtonClick(e.target);
            }
            if (e.target instanceof HTMLElement) {
                const symbol = e.target.closest("[data-symbol-id]");
                if (symbol instanceof HTMLElement) {
                    handleSymbolClick(symbol);
                }
            }
        });
    }
    private static outfitImages: Record<string, HTMLImageElement> = {};
    private static animationFrame = 0;
    private static animatePreviews() {
        for (const $el of <HTMLElement[]>Array.from(document.querySelectorAll(".outfit-preview"))) {
            const url = MargoURLs.playerPath + $el.dataset.outfit;
            let img = this.outfitImages[url];
            if (!img) {
                img = new Image();
                img.src = url;
                this.outfitImages[url] = img;
            }
            if (img.complete) {
                const h = img.height / 4;
                const w = img.width / 4;
                const x = outfitPreviewOffsets[this.animationFrame][0] * w + "px";
                const y = outfitPreviewOffsets[this.animationFrame][1] * h + "px";
                const oy = Math.max(-10, 74 - h);
                if (w > 48) {
                    $el.style.width = `${w}px`;
                }
                $el.innerHTML = `<a target="_blank" href="${url}">
                <div class="outfit" style="margin-top: ${oy}px; width: ${w}px; height: ${h}px; background: url(${url}); background-position: ${x} ${y};"></div>
                </a>`;
            }
        }
        this.animationFrame += 1;
        if (this.animationFrame >= outfitPreviewOffsets.length) {
            this.animationFrame = 0;
        }
    }

    item: StoredItem;
    stat: Record<string, string> = null;
    data: Record<string, any> = {};
    private html = "";
    private $el: HTMLElement;
    private extendedId: ItemExtendedID;
    constructor(target: HTMLElement, item: StoredItem) {
        this.item = item;
        this.$el = target;
        this.stat = parseItemStat(item.stat);
        this.extendedId = parseItemExtendedID(item.extendedID ?? String(item.id));
        for (const key in this.stat) {
            if (statHandlers[key]) {
                statHandlers[key](this);
            }
        }
        for (const tag of item.tags ?? []) {
            if (tagHandlers[tag]) {
                tagHandlers[tag](this);
            }
        }
        if (eqItemClasses.includes(item.cl)) {
            this.addLabel("Poziom ulepszenia:");
            // this.addHTML(`<input type="number" class="upgrade-change-input" value="${this.stat.enhancement_upgrade_lvl ?? "0"}"> `);
            // this.addHTML(`<button data-item-info="${item.id}" class="upgrade-change-submit">Ulepsz</button>`)
            this.addHTML(`<div class="d-flex">`);
            // this.addHTML(`<input class="me-1 form-control form-control-sm upgrade-change-input" type="number"  value="${this.stat.enhancement_upgrade_lvl ?? "0"}"> `);
            // this.addHTML(`<button class="btn btn-secondary btn-sm upgrade-change-submit" data-item-info="${item.id}">Ulepsz</button>`)
            const currentLevel = Number(this.stat.enhancement_upgrade_lvl ?? "0");
            for (let i = 0; i <= 5; ++i) {
                const active = currentLevel == i ? " active" : "";
                this.addHTML(
                    `<button data-upgrade="${i}" class="${active} m-1 btn btn-secondary btn-sm upgrade-change-btn" data-item-info="${item.id}">+${i}</button>`,
                );
            }
            this.addHTML(`</div>`);
        }
        const clgrp = getItemClassGroupByClass(item.cl);
        if (clgrp != ItemClassGroup.NONE) {
            this.addLabel("Wybrany bonus:");
            const disabled = this.stat.enhancement_upgrade_lvl == "5" ? "" : "disabled";
            const bonuses = getItemBonusesByClassGroup(clgrp);
            const bonus = this.stat.bonus ? this.stat.bonus.split(",")[0] : "";
            const bonusesHtmlOptions = bonuses.map((b) => `<option ${b == bonus ? "selected" : ""} value="${b}">${getBonusFriendlyName(b)}</option>`).join("");
            this.addHTML(`<div class="d-flex">`);
            this.addHTML(`<select style="max-width:250px;" ${disabled} class="me-1 form-control form-control-sm bonus-change-input" type="number">`);
            this.addHTML(`<option value="">Brak${disabled ? " - ulepsz najpierw na +5" : ""}</option>`);
            this.addHTML(bonusesHtmlOptions);
            this.addHTML(`</select>`);
            this.addHTML(`<button ${disabled} class="btn btn-secondary btn-sm bonus-change-submit" data-item-info="${item.id}">Zmień</button>`);
            this.addHTML(`</div>`);
        }
        if (target.dataset.availableSymbols) {
            const availableSymbols = JSON.parse(target.dataset.availableSymbols) as StoredItem[];
            if (availableSymbols.length) {
                this.addLabel("Wybrany symbol:");
                this.addHTML(`<div class="d-flex symbol-selector">`);
                for (const symbol of availableSymbols) {
                    const activeSymbolClass = symbol.id == this.extendedId.socketContent ? "selected" : "";
                    // ID is negated to prevent link generation
                    symbol.id = -symbol.id;
                    this.addHTML(writeItem(symbol, `data-symbol-id="${-symbol.id}"`, activeSymbolClass));
                }
                this.addHTML(`</div>`);
            }
        }
        if (target.hasAttribute("compare")) {
            const it = getEqItemBySlot(eqItemSlots[item.cl]);
            if (it && it.cl != ItemClass.BLESSING && (it.id != item.id || it.extendedID != item.extendedID)) {
                this.addHTML(
                    `<button data-cl="${item.cl}" class="btn btn-sm btn-secondary compare-item mt-3" data-item-info="${item.id}">Porównaj z przedmiotem z zestawu</button>`,
                );
            }
        }
        this.finalize();
    }
    addLabel(text: string) {
        this.html += `<h3>${text}</h3>`;
    }
    addOutfitPreviews(urls: string[]) {
        this.html += `<div class="outfit-preview-wrapper">`;
        for (const url of urls) {
            this.html += `<div class="outfit-preview" data-outfit="${url}"></div>`;
        }
        this.html += `</div>`;
    }
    finalize() {
        this.$el.innerHTML = this.html;
    }
    addHTML(html: string) {
        this.html += html;
    }
}
